import React from 'react';
import { Globe2, ExternalLink } from 'lucide-react';

interface FooterProps {
  onNavigate?: (view: 'home' | 'pilot' | 'privacy' | 'terms') => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const handleHomeClick = () => {
    if (onNavigate) {
      onNavigate('home');
      // Adding a small delay to ensure any existing home view scrolls to top
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  };

  const handleAnchorClick = (id: string) => {
    if (onNavigate) {
      onNavigate('home');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-zodiac text-white pt-24 pb-12 px-6 md:px-12 border-t border-white/5 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Top Section: Brand & Navigation */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-16 mb-20">
          <div className="max-w-xl">
            <div className="mb-8">
              <span className="text-2xl font-bold text-white tracking-tighter">Groflex</span>
            </div>
            <h4 className="text-white/40 text-base md:text-lg leading-relaxed font-light italic mb-8 max-w-lg">
              "When outcomes matter, execution must be deliberate, explainable, and owned. Groflex helps leaders coordinate decisions and action across complexity before failure compounds."
            </h4>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-px bg-accent/30"></div>
              <p className="text-sm font-bold tracking-tight text-accent">
                Amit Mundra | Founder & CEO
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-x-16 gap-y-12 sm:gap-x-24">
            {/* PLATFORM COLUMN */}
            <div className="shrink-0">
              <h4 className="font-black mb-7 text-[9px] uppercase tracking-[0.5em] text-white/20">Platform</h4>
              <ul className="space-y-4 text-[14px] text-white/40 font-medium tracking-tight">
                <li>
                  <button 
                    onClick={handleHomeClick}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleAnchorClick('platform')}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Product
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleAnchorClick('solutions')}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Solution
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onNavigate?.('pilot')}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Enterprise Pilot
                  </button>
                </li>
              </ul>
            </div>

            {/* COMPANY COLUMN */}
            <div className="shrink-0">
              <h4 className="font-black mb-7 text-[9px] uppercase tracking-[0.5em] text-white/20">Company</h4>
              <ul className="space-y-4 text-[14px] text-white/40 font-medium tracking-tight">
                <li>
                  <a 
                    href="mailto:hello@groflex.ai"
                    className="text-white font-black text-[11px] uppercase tracking-[0.2em] hover:text-accent transition-executive outline-none text-left block"
                  >
                    CONTACT US
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.linkedin.com/company/89208170" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-1.5 hover:text-accent transition-executive"
                  >
                    LinkedIn
                    <ExternalLink className="w-3.5 h-3.5 opacity-30" />
                  </a>
                </li>
                <li>
                  <button 
                    onClick={() => onNavigate?.('privacy')}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => onNavigate?.('terms')}
                    className="hover:text-accent transition-executive outline-none text-left"
                  >
                    Terms of Use
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Global Presence Section */}
        <div className="pt-12 border-t border-white/5 mb-20">
          <div className="flex items-center space-x-2 mb-10">
            <Globe2 className="w-4 h-4 text-accent/50" />
            <h5 className="text-[10px] font-bold uppercase tracking-[0.4em] text-white/30">Global Presence</h5>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-12">
            {[
              { loc: "USA HQ", addr: "Groflex Technology Inc.\n2261 Market Street STE 5943,\nSan Francisco 94114, CA" },
              { loc: "Spain", addr: "Groflex Tech S.L.U.\nCarrer Estruc 9,\nBarcelona, 08002" },
              { loc: "India", addr: "Groflex Technology Pvt. Ltd.\n42, P5, Kudlu Main Rd, Muneshwara Layout,\nHaralukunte, Bengaluru 560068" },
              { loc: "Qatar", addr: "Groflex Technology LLC\nQFC Business Centre, Office No. 8,\nQFC Tower 1, West Bay, Doha" }
            ].map((p, i) => (
              <div key={i} className="group">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent/80 mb-4">{p.loc}</p>
                <address className="not-italic text-[13px] font-medium text-white/40 leading-relaxed group-hover:text-white/70 transition-colors whitespace-pre-line">
                  {p.addr}
                </address>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer Bottom */}
        <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-[11px] text-white/20 font-medium tracking-tight">
          <p>© 2026 Groflex Technology. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};